import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { firstValueFrom, interval, race, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import saveAs from 'file-saver';

import { ThematicPerformanceHistoryComponent } from '../result-sections/thematic-view/thematic-performance-history/thematic-performance-history.component';
import { ThematicInnovationActivityComponent } from '../result-sections/thematic-view/thematic-innovation-activity/thematic-innovation-activity.component';
import { IThematicInnovationMetricKey } from '../../../../core/models/results.model';

// One chart per innovation-activity metric (same keys the backend expects).
const ACTIVITY_METRICS: IThematicInnovationMetricKey[] = [
  'inv_growth_absolute_12m',
  'inv_growth_relative_12m_sia',
  'invention_count_120m',
];

@Component({
  selector: 'app-theme-pdf-report',
  standalone: true,
  imports: [
    CommonModule,
    ThematicPerformanceHistoryComponent,
    ThematicInnovationActivityComponent,
  ],
  templateUrl: './theme-pdf-report.component.html',
  styleUrl: './theme-pdf-report.component.css',
})
export class ThemePdfReportComponent implements AfterViewInit {
  @Input() tabId!: string;
  @Input() tab_name: string | null = null;
  @Input() query: string | null = null;
  @Input() themeParams: any = null;   // the full tab; chart components read .params

  @ViewChild(ThematicPerformanceHistoryComponent) private perfChart?: ThematicPerformanceHistoryComponent;
  @ViewChildren(ThematicInnovationActivityComponent) private activityCharts?: QueryList<ThematicInnovationActivityComponent>;

  readonly ACTIVITY_METRICS = ACTIVITY_METRICS;

  /** Emits this host's tabId when generation finishes (success or failure) so the parent can unmount it. */
  @Output() done = new EventEmitter<string>();

  constructor(private http: HttpClient) {}

  /** Self-driving: capture this theme's charts, POST for the PDF, save it, then signal done. */
  async ngAfterViewInit(): Promise<void> {
    const p = this.themeParams?.params;
    const name = (this.tab_name?.trim() || 'Theme').replace(/[\\/:*?"<>|]+/g, '');
    const isCustom = p?.theme_search_type === 'custom';
    if (!p || (isCustom ? !p.query : !p.cluster_id)) { this.done.emit(this.tabId); return; }
    try {
      if (!await this.whenReady()) throw new Error('Theme report charts did not finish rendering in time');
      const charts = this.getChartImages();
      const body = isCustom
        ? { theme_search_type: 'custom', query: p.query, theme_name: this.tab_name, charts }
        : { theme_search_type: 'cluster', cluster_id: p.cluster_id, cluster_name: p.cluster_name, charts };
      const blob = await firstValueFrom(this.http.post(
        `/backend/thematicView/report/pdf`,
        body,
        {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('current_user_token') },
          responseType: 'blob',
        },
      ));
      saveAs(blob, `${name} - Innovation Report.pdf`);
    } catch (e) {
      console.error('Theme report PDF failed', e);
    } finally {
      this.done.emit(this.tabId);
    }
  }

  /** Resolves once every chart has settled (painted, or reported no-data/failure) and Chart.js
   *  has finished animating. Custom themes recompute the perf-history portfolio on the fly, which
   *  can take several minutes — so we wait on the charts' own state, not a fixed clock. */
  async whenReady(): Promise<boolean> {
    const settled = (c?: { showChart: boolean; showDataMissing: boolean; loadingFailed: boolean }) =>
      !!c && (c.showChart || c.showDataMissing || c.loadingFailed);
    const allSettled = () => {
      const acts = this.activityCharts?.toArray() ?? [];
      return acts.length === ACTIVITY_METRICS.length && settled(this.perfChart) && acts.every(settled);
    };
    const ready = interval(300).pipe(map(allSettled), filter(Boolean), take(1), map(() => true));
    // Backstop: a legit slow render (~6-7 min) still settles via `ready`; hitting this timer means
    // a section genuinely hung, so we return false and the caller aborts rather than ship a partial PDF.
    const ok = await firstValueFrom(race(ready, timer(600000).pipe(map(() => false))));
    await this.settle();
    return ok;
  }

  /** Two animation frames + a delay so Chart.js finishes its draw animation. */
  private settle(): Promise<void> {
    return new Promise(res =>
      requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(res, 1500))),
    );
  }

  /** Collect exact Chart.js PNGs keyed for the backend report. */
  getChartImages(): Record<string, string> {
    const out: Record<string, string> = {};
    const ph = this.perfChart?.getChartImage();
    if (ph) out['perf_history'] = ph;
    this.activityCharts?.forEach(c => {
      const img = c.getChartImage();
      if (c.lockedMetric && img) out['activity_' + c.lockedMetric] = img;
    });
    return out;
  }
}
